import * as React from 'react';

import { type UniqueIdentifier } from '@dnd-kit/core';
import { getLocalTimeZone, today } from '@internationalized/date';
import {
  enumAssignmentStatus,
  enumServiceStatus,
} from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  FloatingToolbar,
  Loading,
  Row,
  Toggle,
  ToggleButtonGroup,
  toastQueue,
} from '@rematter/ui-web';
import { getRemRankValue } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { useRouteAssignMutation } from '../../hooks/mutations/useRouteAssignMutation';
import { useRouteUnassignMutation } from '../../hooks/mutations/useRouteUnassignMutation';
import { useServiceAssignMutation } from '../../hooks/mutations/useServiceAssignMutation';
import { useServiceTransitionMutation } from '../../hooks/mutations/useServiceTransitionMutation';
import { useServiceUnassignMutation } from '../../hooks/mutations/useServiceUnassignMutation';
import { useSetDriverDisplayOrderMutation } from '../../hooks/mutations/useSetDriverDisplayOrderMutation';
import { useUpdateDriverMutation } from '../../hooks/mutations/useUpdateDriverMutation';
import { useUpdateRouteMutation } from '../../hooks/mutations/useUpdateRouteMutation';
import { useUpdateServiceMutation } from '../../hooks/mutations/useUpdateServiceMutation';
import { useDriversQuery } from '../../hooks/queries/useDriversQuery';
import { useHaulersQuery } from '../../hooks/queries/useHaulersQuery';
import { useLoadBoardServicesQuery } from '../../hooks/queries/useLoadBoardServicesQuery';
import { useRoutesQuery } from '../../hooks/queries/useRoutesQuery';
import { useServiceChangesSubscription } from '../../hooks/subscriptions/useServiceChangesSubscription';
import { useTranslations } from '../../i18n/useTranslations';
import {
  $activeTab,
  $addPendingAssignment,
  $assignDriverToLocalService,
  $clearAllPending,
  $clearPendingConflicts,
  $getLocalServices,
  $markPendingError,
  $pendingAssignments,
  $pendingConflicts,
  $pinnedColumnCollapsed,
  $removePendingAssignment,
  $replaceLocalService,
  $searchTerm,
  $selectedDate,
  $setLocalServices,
  $unassignDriverFromLocalService,
  $updateLocalServicePositions,
  $visibleModal,
} from '../../state/loadBoard.localState';
import {
  RouteFormState,
  RouteRecord,
  ServiceFormState,
  ServiceRecord,
} from '../../types';

import { DriverCardHeader } from './DriverCardHeader';
import { KanbanBoard } from './KanbanBoard';
import { TKanbanColumn } from './KanbanColumn';
import { KanbanTask } from './KanbanTask';
import { RouteDialog } from './RouteDialog';
import { ServiceContent } from './ServiceContent';
import { ServiceDialog } from './ServiceDialog';

import { $organization } from '~/shared/providers/auth/identity.localState';

export const LoadBoard = () => {
  const t = useTranslations();
  const [editService, setEditService] = React.useState<
    (ServiceFormState & { id: string; documentNumber?: string }) | null
  >(null);

  const [editRoute, setEditRoute] = React.useState<
    (RouteFormState & { id: string }) | null
  >(null);

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const organization = useAtomValue($organization);
  const searchTerm = useAtomValue($searchTerm);
  const allLocalServices = useAtomValue($getLocalServices);
  const pendingAssignments = useAtomValue($pendingAssignments);
  const pendingConflicts = useAtomValue($pendingConflicts);
  const [activeTab, setActiveTab] = useAtom($activeTab);
  const [pinnedCollapsed, setPinnedCollapsed] = useAtom($pinnedColumnCollapsed);
  const selectedDate = useAtomValue($selectedDate);
  const [visibleModal, setVisibleModal] = useAtom($visibleModal);
  const setLocalServices = useSetAtom($setLocalServices);
  const assignDriverToService = useSetAtom($assignDriverToLocalService);
  const addPendingAssignment = useSetAtom($addPendingAssignment);
  const removePendingAssignment = useSetAtom($removePendingAssignment);
  const clearAllPending = useSetAtom($clearAllPending);
  const clearPendingConflicts = useSetAtom($clearPendingConflicts);
  const markPendingError = useSetAtom($markPendingError);
  const replaceLocalService = useSetAtom($replaceLocalService);
  const unassignDriverFromService = useSetAtom($unassignDriverFromLocalService);
  const updateLocalServicePositions = useSetAtom($updateLocalServicePositions);

  const localServices = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return allLocalServices;
    return allLocalServices.filter((s) => {
      const fields = [
        s.accountName,
        s.accountAddressName,
        s.documentNumber,
        s.address?.streetAddress,
        s.address?.city,
        s.address?.state,
      ];
      return fields.some((f) => f?.toLowerCase().includes(term));
    });
  }, [allLocalServices, searchTerm]);

  const { mutate: assignToDriver, mutateAsync: assignMutateAsync } =
    useServiceAssignMutation();
  const { mutateAsync: transitionMutateAsync } = useServiceTransitionMutation();
  const { mutate: unassignFromDriver, mutateAsync: unassignMutateAsync } =
    useServiceUnassignMutation();
  const { mutate: updateService } = useUpdateServiceMutation();
  const { mutate: updateRoute } = useUpdateRouteMutation();
  const { mutate: routeAssign } = useRouteAssignMutation();
  const { mutate: routeUnassign } = useRouteUnassignMutation();
  const { mutateAsync: updateDriverAsync } = useUpdateDriverMutation();
  const { mutate: setDriverDisplayOrder } = useSetDriverDisplayOrderMutation();

  const {
    data: driversData,
    isLoading: loadingDrivers,
    refetch: refetchDrivers,
  } = useDriversQuery({
    limit: 100,
  });

  const { data: serviceChangesData } = useServiceChangesSubscription();

  const dateFilterParams = React.useMemo(() => {
    const tz = getLocalTimeZone();
    const isToday =
      selectedDate.toString() === today(getLocalTimeZone()).toString();
    const startOfDay = selectedDate.toDate(tz);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = selectedDate.toDate(tz);
    endOfDay.setHours(23, 59, 59, 999);
    return {
      start: isToday ? undefined : startOfDay.toISOString(),
      end: endOfDay.toISOString(),
    };
  }, [selectedDate]);

  const { data: remoteServicesData, isLoading: isLoadingRemoteServices } =
    useLoadBoardServicesQuery(dateFilterParams);

  const drivers = React.useMemo(() => {
    if (loadingDrivers) return [];
    const all = driversData?.drivers || [];
    return [...all].sort((a, b) => {
      if (a.displayOrder && b.displayOrder) {
        return a.displayOrder.localeCompare(b.displayOrder);
      }
      if (a.displayOrder) return -1;
      if (b.displayOrder) return 1;
      return a.firstName.localeCompare(b.firstName);
    });
  }, [driversData, loadingDrivers]);

  const assignedTruckIds = React.useMemo(
    () => drivers.map((d) => d.defaultTruckId).filter(Boolean) as string[],
    [drivers],
  );

  const assignedTrailerIds = React.useMemo(
    () => drivers.map((d) => d.defaultTrailerId).filter(Boolean) as string[],
    [drivers],
  );

  const pinnedColumns = React.useMemo(
    () => [
      {
        id: 'unassigned',
        title:
          activeTab === 'requests'
            ? t.loadBoardView.tabs.requests
            : t.loadBoardView.tabs.unassigned,
        header: () => (
          <ToggleButtonGroup
            selectionMode="single"
            selectedKeys={new Set([activeTab])}
            onSelectionChange={(keys) => {
              const value = Array.from(keys)[0] as 'requests' | 'unassigned';
              if (value) setActiveTab(value);
            }}
          >
            <Toggle id="unassigned" size="sm">
              {t.loadBoardView.tabs.unassigned}
            </Toggle>
            <Toggle id="requests" size="sm">
              {t.loadBoardView.tabs.requests}
            </Toggle>
          </ToggleButtonGroup>
        ),
      },
    ],
    [activeTab, setActiveTab],
  );

  const pinnedEmptyMessage =
    activeTab === 'requests' ? t.loadBoardView.pinnedEmpty : undefined;

  const handleTogglePinned = React.useCallback(() => {
    setPinnedCollapsed((prev) => !prev);
  }, [setPinnedCollapsed]);

  const { data: haulersData } = useHaulersQuery({ searchTerm: '' });

  const routeIds = React.useMemo(
    () =>
      [
        ...new Set(localServices.map((s) => s.routeId).filter(Boolean)),
      ] as string[],
    [localServices],
  );

  const { data: routesData } = useRoutesQuery({
    ids: routeIds.length > 0 ? routeIds : undefined,
    limit: routeIds.length || 1,
  });

  const routeDataMap = React.useMemo(
    () =>
      new Map<string, RouteRecord>(
        routesData?.routes?.map((r) => [r.id, r]) ?? [],
      ),
    [routesData],
  );

  const onServiceEdit = React.useCallback(
    (service: ServiceRecord) => {
      setEditService({
        id: service.id,
        documentNumber: service.documentNumber,
        typeId: service.serviceTypeId,
        status: service.status,
        rank: service.rank,
        priority: service.priority,
        scheduledWindowStart: service.scheduledWindowStart,
        scheduledWindowEnd: service.scheduledWindowEnd,
        internalNotes: service.internalNotes,
        externalNotes: service.externalNotes,
        driverNotes: service.driverNotes,
        driverId: service.driverId,
        truckId: service.truckId,
        trailerId: service.trailerId,
        facilityGroupId: service.facilityGroupId,
        facilityId: service.facilityId,
        accountId: service.accountId,
        accountAddressId: service.accountAddressId,
        contactId: service.contactId,
        haulerId: service.haulerId,
        routeId: service.routeId,
        address: service.address,
        estimatedArrival: service.estimatedArrival,
        assetQuantities: service.assetQuantities,
        routePosition: service.routePosition,
        relationshipType: service.relationshipType,
      });
      setVisibleModal('edit-service');
    },
    [setVisibleModal],
  );

  const createKanbanTask = React.useCallback(
    (service: ServiceRecord): KanbanTask => {
      const route = service.routeId
        ? routeDataMap.get(service.routeId)
        : undefined;
      return {
        id: service.id,
        columnId:
          service.haulerId && service.haulerId !== organization?.id
            ? (service.driverId ?? `hauler-unassigned-${service.haulerId}`)
            : (service.driverId ?? 'unassigned'),
        rank: service.rank,
        routeId: service.routeId,
        routeName: route?.description,
        routeStatus: route?.status,
        routePosition: service.routePosition,
        status: service.status,
        pending: pendingAssignments.has(service.id),
        content: () => <ServiceContent service={service} />,
      };
    },
    [organization?.id, routeDataMap, pendingAssignments],
  );

  const onServiceClick = React.useCallback(
    (taskId: UniqueIdentifier) => {
      const service = localServices.find((s) => s.id === String(taskId));
      if (service) {
        onServiceEdit(service);
      }
    },
    [localServices, onServiceEdit],
  );

  const onRouteClick = React.useCallback(
    (routeId: string) => {
      const route = routeDataMap.get(routeId);
      if (!route) return;
      setEditRoute({
        id: route.id,
        description: route.description,
        status: route.status,
        routeDate: route.routeDate,
        startedAt: route.startedAt,
        completedAt: route.completedAt,
        driverId: route.driverId,
        haulerId: route.haulerId,
        truckId: route.truckId,
        trailerId: route.trailerId,
        facilityGroupId: route.facilityGroupId,
        serviceIds: route.services.map((s) => s.id),
      });
      setVisibleModal('edit-route');
    },
    [routeDataMap, setVisibleModal],
  );

  const kanbanTasks = React.useMemo(() => {
    const tasks = localServices
      .map((service) => {
        const task = createKanbanTask(service);
        if (editService?.id === service.id) {
          return { ...task, selected: true };
        }
        return task;
      })
      .sort((a, b) => a.rank.localeCompare(b.rank));

    if (activeTab === 'requests') {
      return tasks.map((task) =>
        task.columnId === 'unassigned'
          ? { ...task, columnId: '__hidden__' as UniqueIdentifier }
          : task,
      );
    }
    return tasks;
  }, [localServices, createKanbanTask, editService?.id, activeTab]);

  const handleServiceMove = (
    columnId: string,
    serviceId: string,
    newRank: string,
  ) => {
    const isUnassignedTarget =
      columnId === 'unassigned' || columnId.startsWith('hauler-unassigned-');
    const isPending = pendingAssignments.has(serviceId);

    if (isUnassignedTarget) {
      if (isPending) {
        removePendingAssignment(serviceId);
      } else {
        const service = localServices.find((s) => s.id === serviceId);
        unassignFromDriver({ id: serviceId });
        if (service?.status === enumServiceStatus.Planned) {
          transitionMutateAsync({
            id: serviceId,
            status: enumServiceStatus.Draft,
          }).catch(() => {
            toastQueue.add(
              { title: t.loadBoardView.errors.revertToDraft, variant: 'error' },
              { timeout: 8000 },
            );
          });
        }
      }
      unassignDriverFromService({ id: serviceId, rank: newRank });
    } else if (isPending) {
      addPendingAssignment({
        serviceId,
        driverId: columnId,
        rank: newRank,
      });
      assignDriverToService({
        id: serviceId,
        driverId: columnId,
        rank: newRank,
      });
    } else {
      const service = localServices.find((s) => s.id === serviceId);
      const isSameColumn = service?.driverId === columnId;
      if (isSameColumn) {
        assignDriverToService({
          id: serviceId,
          driverId: columnId,
          rank: newRank,
        });
        updateService({ id: serviceId, rank: newRank });
      } else if (service?.driverId) {
        assignToDriver({
          id: serviceId,
          assignmentStatus: enumAssignmentStatus.Accepted,
          driverId: columnId,
        });
        assignDriverToService({
          id: serviceId,
          driverId: columnId,
          rank: newRank,
        });
      } else {
        addPendingAssignment({
          serviceId,
          driverId: columnId,
          rank: newRank,
        });
        assignDriverToService({
          id: serviceId,
          driverId: columnId,
          rank: newRank,
        });
      }
    }
  };

  const submitServices = React.useCallback(
    async (serviceIds: string[]) => {
      setIsSubmitting(true);
      try {
        for (const serviceId of serviceIds) {
          const assignment = pendingAssignments.get(serviceId);
          if (!assignment) continue;

          try {
            await assignMutateAsync({
              id: serviceId,
              assignmentStatus: enumAssignmentStatus.Accepted,
              driverId: assignment.driverId,
            });

            const service = localServices.find((s) => s.id === serviceId);
            const needsTransition =
              service?.status !== enumServiceStatus.Planned;

            try {
              if (needsTransition) {
                await transitionMutateAsync({
                  id: serviceId,
                  status: enumServiceStatus.Planned,
                });
              }
              removePendingAssignment(serviceId);
            } catch {
              try {
                await unassignMutateAsync({ id: serviceId });
                toastQueue.add(
                  {
                    title: t.loadBoardView.errors.transitionFailed,
                    variant: 'error',
                  },
                  { timeout: 8000 },
                );
              } catch {
                markPendingError(serviceId);
                toastQueue.add(
                  {
                    title: t.loadBoardView.errors.rollbackFailed,
                    variant: 'error',
                  },
                  { timeout: 12000 },
                );
              }
            }
          } catch {
            toastQueue.add(
              {
                title: t.loadBoardView.errors.assignFailed,
                variant: 'error',
              },
              { timeout: 8000 },
            );
          }
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      pendingAssignments,
      assignMutateAsync,
      transitionMutateAsync,
      unassignMutateAsync,
      removePendingAssignment,
      markPendingError,
    ],
  );

  const handleSubmitAll = React.useCallback(() => {
    const serviceIds = Array.from(pendingAssignments.keys());
    submitServices(serviceIds);
  }, [pendingAssignments, submitServices]);

  const handleSubmitForDriver = React.useCallback(
    (driverId: string) => {
      const serviceIds = Array.from(pendingAssignments.entries())
        .filter(([, assignment]) => assignment.driverId === driverId)
        .map(([serviceId]) => serviceId);
      submitServices(serviceIds);
    },
    [pendingAssignments, submitServices],
  );

  const handleUpdateDefaultTruck = React.useCallback(
    async (driverId: string, truckId: string | null) => {
      await updateDriverAsync({ id: driverId, defaultTruckId: truckId });
      refetchDrivers();
    },
    [updateDriverAsync, refetchDrivers],
  );

  const handleUpdateDefaultTrailer = React.useCallback(
    async (driverId: string, trailerId: string | null) => {
      await updateDriverAsync({ id: driverId, defaultTrailerId: trailerId });
      refetchDrivers();
    },
    [updateDriverAsync, refetchDrivers],
  );

  const handleColumnReorder = React.useCallback(
    (columnIds: string[]) => {
      const previousOrder = drivers.map((d) => d.id);
      const movedId = columnIds.find((id, i) => previousOrder[i] !== id);
      if (!movedId) return;

      const newIndex = columnIds.indexOf(movedId);
      const rank = getRemRankValue(
        columnIds.map((id) => {
          const driver = drivers.find((d) => d.id === id);
          return { id, displayOrder: driver?.displayOrder ?? '' };
        }),
        newIndex,
        (item) => item.displayOrder,
      );

      setDriverDisplayOrder(
        { driverId: movedId, rank },
        {
          onError: () => {
            toastQueue.add(
              {
                title: t.loadBoardView.errors.columnOrderFailed,
                variant: 'error',
              },
              { timeout: 8000 },
            );
            refetchDrivers();
          },
        },
      );
    },
    [drivers, setDriverDisplayOrder, refetchDrivers],
  );

  const handleCreateTaskForDriver = React.useCallback(
    (_driverId: string) => {
      setEditService(null);
      setVisibleModal('create-service');
    },
    [setVisibleModal],
  );

  const driverColumns: TKanbanColumn[] = React.useMemo(() => {
    if (loadingDrivers) return [];

    return drivers.map((driver) => ({
      id: driver.id,
      title: `${driver.firstName} ${driver.lastName}`,
      header: () => (
        <DriverCardHeader
          driver={driver}
          excludeTruckIds={assignedTruckIds}
          excludeTrailerIds={assignedTrailerIds}
          onUpdateDefaultTruck={(truckId) =>
            handleUpdateDefaultTruck(driver.id, truckId)
          }
          onUpdateDefaultTrailer={(trailerId) =>
            handleUpdateDefaultTrailer(driver.id, trailerId)
          }
          onCreateTask={() => handleCreateTaskForDriver(driver.id)}
          onSubmitToQueue={() => handleSubmitForDriver(driver.id)}
        />
      ),
    }));
  }, [
    drivers,
    loadingDrivers,
    handleUpdateDefaultTruck,
    handleUpdateDefaultTrailer,
    handleCreateTaskForDriver,
    handleSubmitForDriver,
    assignedTruckIds,
    assignedTrailerIds,
  ]);

  const handleRouteGroupMove = (
    columnId: string,
    routeId: string,
    _taskIds: string[],
  ) => {
    if (
      columnId === 'unassigned' ||
      columnId.startsWith('hauler-unassigned-')
    ) {
      routeUnassign({ id: routeId });
    } else {
      routeAssign({
        id: routeId,
        assignmentStatus: enumAssignmentStatus.Accepted,
        driverId: columnId,
      });
    }
  };

  const handleRouteReorder = React.useCallback(
    (routeId: string, orderedTaskIds: string[]) => {
      updateLocalServicePositions({
        routeId,
        orderedServiceIds: orderedTaskIds,
      });
      updateRoute({ id: routeId, serviceIds: orderedTaskIds });
    },
    [updateLocalServicePositions, updateRoute],
  );

  const handleServiceDialogClose = () => {
    setEditService(null);
    setVisibleModal(undefined);
  };

  const handleRouteDialogClose = () => {
    setEditRoute(null);
    setVisibleModal(undefined);
  };

  const handleRouteDialogSuccess = React.useCallback(
    (data?: { routeId: string; serviceIds: string[] }) => {
      if (data && data.serviceIds.length > 0) {
        updateLocalServicePositions({
          routeId: data.routeId,
          orderedServiceIds: data.serviceIds,
        });
      }
      handleRouteDialogClose();
    },
    [updateLocalServicePositions],
  );

  React.useEffect(() => {
    if (serviceChangesData) {
      replaceLocalService(serviceChangesData);
    }
  }, [serviceChangesData, replaceLocalService]);

  React.useEffect(() => {
    if (remoteServicesData) {
      setLocalServices(remoteServicesData);
    }
  }, [remoteServicesData, setLocalServices]);

  const previousDateRef = React.useRef(selectedDate);
  React.useEffect(() => {
    if (previousDateRef.current.toString() !== selectedDate.toString()) {
      clearAllPending();
      previousDateRef.current = selectedDate;
    }
  }, [selectedDate, clearAllPending]);

  React.useEffect(() => {
    if (pendingConflicts.length > 0) {
      for (const serviceId of pendingConflicts) {
        toastQueue.add(
          {
            title: `Service ${serviceId} was modified externally while pending.`,
            variant: 'warning',
          },
          { timeout: 6000 },
        );
      }
      clearPendingConflicts();
    }
  }, [pendingConflicts, clearPendingConflicts]);

  const pendingCount = pendingAssignments.size;

  return (
    <div className="@LoadBoard contents">
      <Loading isLoading={loadingDrivers || isLoadingRemoteServices}>
        <KanbanBoard
          columns={driverColumns}
          pinnedColumns={pinnedColumns}
          haulers={haulersData?.haulers}
          tasks={kanbanTasks}
          pinnedEmptyMessage={pinnedEmptyMessage}
          pinnedCollapsed={pinnedCollapsed}
          onTogglePinned={handleTogglePinned}
          onTaskClick={onServiceClick}
          onRouteClick={onRouteClick}
          onTaskMove={handleServiceMove}
          onRouteGroupMove={handleRouteGroupMove}
          onRouteReorder={handleRouteReorder}
          onSubmitForDriver={handleSubmitForDriver}
          onColumnReorder={handleColumnReorder}
        />
      </Loading>

      <FloatingToolbar visible={pendingCount > 0}>
        <Row xBetween gap3>
          <Row gap2 className="text-sm">
            <Badge color="warning" size="small">
              {pendingCount}
            </Badge>
            {t.loadBoardView.pendingToolbar.message}
          </Row>
          <Button
            variant="solid"
            size="small"
            isDisabled={isSubmitting}
            onPress={handleSubmitAll}
          >
            {isSubmitting
              ? t.loadBoardView.pendingToolbar.submitting
              : t.loadBoardView.pendingToolbar.submitAll}
          </Button>
        </Row>
      </FloatingToolbar>

      <ServiceDialog
        isOpen={
          visibleModal === 'create-service' || visibleModal === 'edit-service'
        }
        onClose={handleServiceDialogClose}
        onSuccess={handleServiceDialogClose}
        service={editService || undefined}
      />

      <RouteDialog
        isOpen={
          visibleModal === 'create-route' || visibleModal === 'edit-route'
        }
        onClose={handleRouteDialogClose}
        onSuccess={handleRouteDialogSuccess}
        route={editRoute || undefined}
      />
    </div>
  );
};
